import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Heading, Flex, Box, Accordion, SimpleGrid, useDisclosure, Button, Divider, Spacer } from '@chakra-ui/react';
import { Formik, Form } from 'formik';
import { useParams } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from '@emotion/styled/macro';

import { loadModules } from 'esri-loader';

import {
    getLoadDefinitions,
    deleteLoadDefinition,
    editLoad,
    deleteLoadItem,
    createLoadDefinition,
} from '../../services/loads';
import { checkCoordsDigits } from '../../services/portfolios';
import {
    hasAppliedCustomFilters,
    getDataFilters,
    checkItemInputEvent,
    flatItemDescriptions,
} from '../../services/items';

import LoadDefinition from './LoadDefinition';

import useCommonToast from '../../hooks/useCommonToast';
import useHistoryPush from '../../hooks/useHistoryPush';

import ExpandableItemWrapper from '../layout/ExpandableItemWrapper';
import ShowHideSidebarButton from '../layout/ShowHideSidebarButton';

import InputFormikField from '../forms/InputFormikField';
import ComboboxFormikField from '../forms/ComboboxFormikField';

import ItemFilters from '../utils/ItemFilters';
import Error from '../utils/Error';
import SideDrawer from '../utils/SideDrawer';
import DefinitionsSkeleton from '../utils/DefinitionsSkeleton';
import AddSubItem from '../utils/AddSubItem';
import AddSubItemModal from '../utils/AddSubItemModal';
import ConfirmationModal from '../utils/ConfirmationModal';
import DeleteRecordModal from '../utils/DeleteRecordModal';
import ItemPropertiesModal from '../utils/ItemPropertiesModal';
import CircularIconButton from '../utils/CircularIconButton';
import SecondaryIconButton from '../utils/SecondaryIconButton';
import Tooltip from '../utils/Tooltip';

import { ReactComponent as PowerCableIcon } from '../../icons/power-cable.svg';
import { ReactComponent as SaveIcon } from '../../icons/save.svg';
import { ReactComponent as DeleteLoadIcon } from '../../icons/delete.svg';
import { ReactComponent as EditIcon } from '../../icons/edit-circled.svg';
import { ReactComponent as FilterIcon } from '../../icons/filter.svg';
import { ReactComponent as FileIcon } from '../../icons/file.svg';

// Load component is used in Loads and Portfolios
// onLoadEdit, onLoadDelete, typeId props are active if the Load component is called from Loads
// loadItem, onNodeRename, onNodeOwnershipPercentageUpdate, onNodeItemDelete props are active if the Load component is called from Portfolios

const Load = ({
    onLoadEdit,
    onLoadDelete,
    typeId,
    loadItem,
    onNodeRename,
    onNodeOwnershipPercentageUpdate,
    onNodeItemDelete,
    refreshMapLayer,
}) => {
    const { loadId } = useParams();
    const toast = useCommonToast();
    const intl = useIntl();
    const historyPush = useHistoryPush();

    const deleteModal = useDisclosure();
    const deleteItemModal = useDisclosure();
    const filterDrawer = useDisclosure();
    const propertiesModal = useDisclosure();
    const addSubItemModal = useDisclosure();

    const itemId = Number(loadId || loadItem.properties.id);
    const itemTypeId = typeId || loadItem.properties.typeId;
    const item = 'load';

    const [loadDefinitions, setLoadDefinitions] = useState(null);
    const [selectedLoad, setSelectedLoad] = useState(null);
    const [filters, setFilters] = useState(() => getDataFilters(item));

    const [loadingError, setLoadingError] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [subloadForDeletion, setSubloadForDeletion] = useState(null);
    const [editDescriptionConfig, setEditDescriptionConfig] = useState(null);

    const selectedLoadRef = useRef(null);

    const formLabels = {
        book: intl.formatMessage({ id: 'load_book' }),
        pricingPoint: intl.formatMessage({ id: 'load_pricing_point' }),
        percentOwned: intl.formatMessage({ id: 'load_percent_owned' }),
        modelType: intl.formatMessage({ id: 'load_definitions_model_type' }),
        weatherZone: intl.formatMessage({ id: 'load_definitions_weather_zone' }),
        dataWeight: intl.formatMessage({ id: 'load_definitions_data_weighting' }),
        retailPriceItem: intl.formatMessage({ id: 'load_definitions_retail_price_item' }),
        native: intl.formatMessage({ id: 'load_definitions_native' }),
        nativeOptionTrue: intl.formatMessage({ id: 'load_definitions_native_option_true' }),
        nativeOptionFalse: intl.formatMessage({ id: 'load_definitions_native_option_false' }),
        selectPlaceholder: intl.formatMessage({ id: 'common_forms_select_option' }),
        latitude: intl.formatMessage({ id: 'latitude' }),
        longitude: intl.formatMessage({ id: 'longitude' }),
    };

    const initialFormValues = {
        description: selectedLoad?.description,
        bookId: selectedLoad?.bookId,
        pricingPointId: selectedLoad?.pricingPointId || '',
        definitions: loadDefinitions?.loadDefinitionResponses.length > 0 && {},
        latitude: selectedLoad?.latitude !== null ? selectedLoad?.latitude : '',
        longitude: selectedLoad?.longitude !== null ? selectedLoad?.longitude : '',
    };

    const nativeOptions = useMemo(
        () => [
            { id: 0, description: formLabels.nativeOptionTrue },
            { id: -1, description: formLabels.nativeOptionFalse },
        ],
        [formLabels.nativeOptionTrue, formLabels.nativeOptionFalse]
    );

    loadDefinitions?.loadDefinitionResponses.forEach((def) => {
        initialFormValues.definitions[def.id] = {};

        initialFormValues.definitions[def.id]['loadId'] = def.id;
        initialFormValues.definitions[def.id]['description'] = def.description;
        initialFormValues.definitions[def.id]['modelWeightId'] = def.modelWeightId || '';
        initialFormValues.definitions[def.id]['moduleProcessId'] = def.moduleProcessId || '';
        initialFormValues.definitions[def.id]['retailPriceItemId'] = def.retailPriceItemId || '';
        initialFormValues.definitions[def.id]['weatherZoneId'] = def.weatherZoneId || '';
        initialFormValues.definitions[def.id]['nativeLoad'] = def.nativeLoad ? 0 : -1; // Options are 0 or -1

        return initialFormValues.definitions[def.id];
    });

    const fetchLoadDefinitions = useCallback(() => {
        return getLoadDefinitions(itemId)
            .then((data) => {
                setSelectedLoad(data.loadItem);
                selectedLoadRef.current = data.loadItem;
                setLoadDefinitions({ ...data, nativeOptions });
                setEditDescriptionConfig(flatItemDescriptions(data.loadDefinitionResponses, itemId));

                // This is needed to show results after previous error
                setLoadingError(false);
            })
            .catch(() => setLoadingError(true))
            .finally(() => setIsLoading(false));
    }, [itemId, nativeOptions]);

    useEffect(() => {
        setIsLoading(true);
        fetchLoadDefinitions();
    }, [fetchLoadDefinitions]);

    const onDeleteModalOpen = (subloadId, subloadDesc) => {
        setSubloadForDeletion({
            id: subloadId,
            description: subloadDesc,
        });
        deleteModal.onOpen();
    };

    const onDeleteModalClose = () => {
        deleteModal.onClose();
        setSubloadForDeletion(null);
    };

    const onSubLoadDelete = async (subloadId) => {
        onDeleteModalClose();

        const successMessage = intl.formatMessage({ id: 'common_delete_success' }, { item: 'sub-load' });
        const errorMessage = intl.formatMessage({ id: 'common_generic_saving_error' });

        try {
            setIsLoading(true);

            await deleteLoadDefinition(subloadId);
            await fetchLoadDefinitions();

            toast(successMessage);
        } catch (error) {
            if (error.response.status !== 401) {
                toast({
                    status: 'error',
                    message: errorMessage,
                });
            }
        } finally {
            setIsLoading(false);
        }
    };

    // Move validateDescription
    // Move loadModules to services
    // Add all this to hedges
    const validateDescription = (value, selectedLoadId) => {
        const trimmed = value.trim();
        let errorMessage = '';

        if (trimmed.length === 0) {
            errorMessage = intl.formatMessage({ id: 'common_forms_validation_required' }, { label: 'Load name' });
        } else if (trimmed.length > 100) {
            errorMessage = intl.formatMessage(
                { id: 'common_forms_validation_length' },
                { label: 'Load name', lengthRule: '100 characters or less' }
            );
        }

        if (errorMessage !== '') {
            setEditDescriptionConfig((prevState) => {
                return { ...prevState, [selectedLoadId]: { ...prevState[selectedLoadId], penDisabled: true } };
            });
        } else {
            setEditDescriptionConfig((prevState) => {
                return { ...prevState, [selectedLoadId]: { ...prevState[selectedLoadId], penDisabled: false } };
            });
        }

        return errorMessage;
    };

    const onLoadFormSubmit = async ({
        description,
        bookId,
        pricingPointId: ppId,
        definitions,
        longitude,
        latitude,
    }) => {
        const successMessage = intl.formatMessage({ id: 'load_save_changes_success' });
        const errorMessage = intl.formatMessage({ id: 'common_generic_saving_error' });

        const [webMercatorUtils] = await loadModules(['esri/geometry/support/webMercatorUtils']);
        const [x, y] = webMercatorUtils.lngLatToXY(longitude, latitude);

        const pricingPointId = ppId || null;
        const subLoads = [];

        Object.values(definitions).forEach((subLoad) =>
            subLoads.push({
                ...subLoad,
                nativeLoad: subLoad.nativeLoad === 0 ? true : false,
                retailPriceItemId: subLoad.retailPriceItemId || null,
            })
        );

        try {
            await editLoad({
                load: {
                    description,
                    bookId,
                    itemTypeId,
                    pricingPointId,
                    itemId,
                    longitude: longitude !== '' ? longitude : null,
                    latitude: latitude !== '' ? latitude : null,
                    x: longitude !== '' ? x : null,
                    y: latitude !== '' ? y : null,
                },
                subLoads,
            });

            await fetchLoadDefinitions();

            if (!loadItem) {
                onLoadEdit({
                    id: selectedLoad.id,
                    description,
                    bookId,
                    pricingPointId,
                    securityId: selectedLoad.securityId,
                });
            } else {
                onNodeRename(description);
            }

            toast(successMessage);
        } catch (error) {
            if (error.response.status !== 401) {
                toast({
                    status: 'error',
                    message: errorMessage,
                });
            }
        }
        if (typeof refreshMapLayer === 'function') {
            refreshMapLayer({ long: longitude, lat: latitude, objectRefId: selectedLoadRef.current.objectRefId });
        }
    };

    const onItemDelete = async (targetLoad) => {
        const successMessage = intl.formatMessage({ id: 'common_delete_success' }, { item: 'load item' });
        const errorMessage = intl.formatMessage({ id: 'common_generic_saving_error' });

        try {
            await deleteLoadItem(targetLoad.id);

            //check whether the load item is deleted from Load or Portfolios
            if (loadItem) {
                onNodeItemDelete();
            } else {
                onLoadDelete(targetLoad);
                historyPush('/loads');
            }

            toast(successMessage);
        } catch (error) {
            if (error.response.status !== 401) {
                toast({
                    status: 'error',
                    message: errorMessage,
                });
            }
        }
    };

    const onApplyFilters = (newFilters) => {
        filterDrawer.onClose();

        setFilters(newFilters);
    };

    const onEditDescription = (loadId) => {
        if (!editDescriptionConfig[loadId].penDisabled) {
            setEditDescriptionConfig((prevState) => {
                return {
                    ...prevState,
                    [loadId]: { isActive: !prevState[loadId].isActive, penDisabled: false },
                };
            });
        }
    };

    const onAddSubItem = (subLoad) => {
        addSubItemModal.onClose();

        setEditDescriptionConfig((prevState) => ({
            ...prevState,
            [subLoad.id]: { isActive: false, penDisabled: false },
        }));

        setLoadDefinitions((prev) => ({
            ...prev,
            loadDefinitionResponses: [...prev.loadDefinitionResponses, subLoad],
        }));
    };

    const validate = (values) => {
        let longitude = validateLongLat(values.longitude, 'long', values.latitude);
        let latitude = validateLongLat(values.latitude, 'lat', values.longitude);

        return {
            ...(longitude ? { longitude } : null),
            ...(latitude ? { latitude } : null),
        };
    };

    const validateLongLat = (val, type, sibling) => {
        let errorMessage;

        if (type === 'long') {
            if (val >= 180 || val <= -180) {
                errorMessage = intl.formatMessage({ id: 'longitude_validation_borders' });
            }
        }
        if (type === 'lat') {
            if (val > 85 || val < -85) {
                errorMessage = intl.formatMessage({ id: 'latitude_validation_borders' });
            }
        }

        if (val !== '' && sibling === '') {
            errorMessage = intl.formatMessage({ id: 'longlat_validation_both' });
        }

        let digitsError = checkCoordsDigits(val);
        if (digitsError) {
            errorMessage = intl.formatMessage({ id: 'longlat_validation_decimal' });
        }

        return errorMessage;
    };

    return (
        <ExpandableItemWrapper>
            {isLoading ? (
                <DefinitionsSkeleton />
            ) : loadingError ? (
                <Box m={6}>
                    <Error primaryId="common_error" secondaryId="common_loading_error" additionalText="definitions" />
                </Box>
            ) : (
                <>
                    <Formik
                        validateOnChange={false}
                        enableReinitialize
                        initialValues={initialFormValues}
                        onSubmit={onLoadFormSubmit}
                        validate={validate}
                    >
                        {({ isSubmitting, dirty, isValid, values }) => (
                            <Form>
                                <Flex pt={3} pl={6} align="center">
                                    <CustomPowerCableIcon isMaster={selectedLoad.securityId} />
                                    {!editDescriptionConfig[selectedLoad.id]?.isActive ? (
                                        <Heading px={2} as="h2" variant="h2">
                                            {values.description}
                                        </Heading>
                                    ) : (
                                        <InputFormikField
                                            px={3}
                                            flex="1"
                                            maxW="500px"
                                            validate={(value) => validateDescription(value, selectedLoad.id)}
                                            name="description"
                                            type="text"
                                            onKeyDown={checkItemInputEvent}
                                            isFastField
                                        />
                                    )}

                                    <Box display="inline-block" mr={4}>
                                        <Tooltip
                                            label={intl.formatMessage({ id: 'common_edit_btn_tooltip_and_label' })}
                                        >
                                            <CircularIconButton
                                                aria-label={intl.formatMessage({
                                                    id: 'common_edit_btn_tooltip_and_label',
                                                })}
                                                leftIcon={<EditIcon />}
                                                onClick={() => onEditDescription(selectedLoad.id)}
                                            />
                                        </Tooltip>
                                    </Box>
                                </Flex>

                                <ActionBar>
                                    <ShowHideSidebarButton />

                                    <SecondaryIconButton
                                        leftIcon={<FileIcon />}
                                        size="sm"
                                        variant="solid"
                                        onClick={propertiesModal.onOpen}
                                        flexShrink={0}
                                    >
                                        <FormattedMessage id="portfolio_item_properties" />
                                    </SecondaryIconButton>

                                    <SecondaryIconButton
                                        leftIcon={<CustomFilterIcon />}
                                        rightIcon={hasAppliedCustomFilters(filters) && <CustomFilterIndicatorIcon />}
                                        type="button"
                                        variant="secondary"
                                        onClick={filterDrawer.onOpen}
                                        size="sm"
                                    >
                                        <FormattedMessage id="common_filters_btn" values={{ item: 'Load' }} />
                                    </SecondaryIconButton>

                                    <Spacer display={{ base: 'none', xl: 'block' }} />

                                    <Button
                                        leftIcon={<DeleteLoadIcon />}
                                        type="button"
                                        variant="special"
                                        onClick={deleteItemModal.onOpen}
                                        size="sm"
                                    >
                                        <FormattedMessage id="common_delete_item_btn" />
                                    </Button>

                                    <Button
                                        leftIcon={<SaveIcon />}
                                        isDisabled={!isValid || !dirty}
                                        isLoading={isSubmitting}
                                        type="submit"
                                        size="sm"
                                    >
                                        <FormattedMessage id="common_save_changes_button" />
                                    </Button>
                                </ActionBar>

                                <Divider />

                                <SimpleGrid pt={7} pb={5} px={6} columns={{ xl: 4 }} spacingX={6} spacingY={4}>
                                    <ComboboxFormikField
                                        id="bookId"
                                        name="bookId"
                                        label={formLabels.book}
                                        options={loadDefinitions.books}
                                        valueKey="id"
                                        labelKey="name"
                                        isFastField
                                    />

                                    <ComboboxFormikField
                                        id="pricingPointId"
                                        name="pricingPointId"
                                        label={formLabels.pricingPoint}
                                        info={<FormattedMessage id="common_pricing_point_empty_tooltip" />}
                                        showPlaceholder={true}
                                        placeholderValue=""
                                        options={loadDefinitions.pricingPoints}
                                        valueKey="id"
                                        labelKey="description"
                                        isFastField
                                    />

                                    <InputFormikField
                                        id="latitude"
                                        name="latitude"
                                        label={formLabels.latitude}
                                        type="number"
                                        isFastField
                                    />

                                    <InputFormikField
                                        id="longitude"
                                        name="longitude"
                                        label={formLabels.longitude}
                                        type="number"
                                        isFastField
                                    />
                                </SimpleGrid>

                                <Box>
                                    {loadDefinitions.loadDefinitionResponses.length === 0 ? (
                                        <AddSubItem onOpen={addSubItemModal.onOpen} />
                                    ) : (
                                        <CustomAccordion allowToggle defaultIndex={[0]}>
                                            {loadDefinitions.loadDefinitionResponses.map((def, idx) => {
                                                const itemId = def.id;
                                                return (
                                                    <LoadDefinition
                                                        key={itemId}
                                                        itemLabel={item}
                                                        itemId={itemId}
                                                        idx={idx}
                                                        definitions={loadDefinitions}
                                                        formLabels={formLabels}
                                                        filters={filters}
                                                        onDeleteModalOpen={onDeleteModalOpen}
                                                        editDescriptionConfig={editDescriptionConfig}
                                                        onEditDescription={onEditDescription}
                                                        setEditDescriptionConfig={setEditDescriptionConfig}
                                                        onAddSubItemModalOpen={addSubItemModal.onOpen}
                                                    />
                                                );
                                            })}
                                        </CustomAccordion>
                                    )}
                                </Box>
                            </Form>
                        )}
                    </Formik>

                    {deleteModal.isOpen && (
                        <DeleteRecordModal
                            onClose={onDeleteModalClose}
                            headerItem="Sub-load"
                            contentMessageItem={subloadForDeletion.description}
                            onConfirm={() => {
                                onSubLoadDelete(subloadForDeletion.id);
                            }}
                        />
                    )}

                    {deleteItemModal.isOpen && (
                        <ConfirmationModal
                            isOpen
                            onClose={deleteItemModal.onClose}
                            header={<FormattedMessage id="common_delete_modal_heading" values={{ item: 'Item' }} />}
                            hasExtraStep
                            content={
                                <FormattedMessage
                                    id="common_delete_modal_msg"
                                    values={{ item: selectedLoad.description }}
                                />
                            }
                            confirmText={
                                <Box as="span" textTransform="capitalize">
                                    <FormattedMessage id="common_delete" />
                                </Box>
                            }
                            onConfirm={() => {
                                deleteItemModal.onClose();
                                onItemDelete(selectedLoad);
                            }}
                        />
                    )}

                    {propertiesModal.isOpen && (
                        <ItemPropertiesModal
                            isOpen
                            onClose={propertiesModal.onClose}
                            itemId={selectedLoad.id}
                            portfolio={loadItem}
                            type="load"
                            onNodeOwnershipPercentageUpdate={onNodeOwnershipPercentageUpdate}
                        />
                    )}

                    {filterDrawer.isOpen && (
                        <SideDrawer
                            isOpen
                            onClose={filterDrawer.onClose}
                            header={<FormattedMessage id="common_filter_heading" values={{ item: 'Load' }} />}
                            content={<ItemFilters filters={filters} onApplyFilters={onApplyFilters} item={item} />}
                            placement="right"
                        />
                    )}

                    {addSubItemModal.isOpen && (
                        <AddSubItemModal
                            isOpen
                            item="Load"
                            onClose={addSubItemModal.onClose}
                            definitions={loadDefinitions}
                            formLabels={formLabels}
                            selectedLoad={selectedLoad}
                            onCreateSubItem={createLoadDefinition}
                            onAddSubItem={onAddSubItem}
                        />
                    )}
                </>
            )}
        </ExpandableItemWrapper>
    );
};

const CustomFilterIndicatorIcon = styled.div`
    background: var(--chakra-colors-blue-400);
    height: 6px;
    width: 6px;
    border-radius: var(--chakra-radii-md);
    margin-top: -10px;
`;

const CustomFilterIcon = styled(FilterIcon)`
    path {
        fill: #677380;
    }
`;

const CustomPowerCableIcon = styled(PowerCableIcon, { shouldForwardProp: (prop) => prop !== 'isMaster' })`
    height: 32px;
    width: 32px;
    flex-shrink: 0;

    path {
        fill: ${(props) => (props.isMaster ? 'var(--chakra-colors-green-500)' : 'var(--chakra-colors-gray-600)')};
    }
`;

const ActionBar = styled.div`
    display: flex;
    flex-wrap: wrap;
    flex-direction: column;
    width: 100%;
    padding: 12px 24px;
    gap: 16px;

    button {
        text-transform: capitalize;
        width: 100%;
    }

    button:first-of-type {
        margin-top: 4px;
    }

    @media (min-width: 769px) {
        flex-direction: row;

        button:first-of-type {
            margin-top: 0px;
        }
    }

    @media (min-width: 1280px) {
        button {
            width: auto;
        }
    }
`;

const CustomAccordion = styled(Accordion)`
    .chakra-accordion__item:last-child {
        border-bottom: 1px solid var(--chakra-colors-gray-200);
    }
`;

export default Load;
